import { useCallback, useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import * as Font from "expo-font";
import {
  fontNameToSlug,
  slugToFontFamily,
  isSystemFont,
  hasFontSource,
} from "@utils/engraveFonts";
import { getNativeFontAsset } from "@utils/engraveFontAssets";

export interface FontOption {
  label: string;
  value: string;
  fontUrl?: string;
}

interface UseEngraveFontsReturn {
  fontsLoaded: boolean;
  loadingFonts: Set<string>;
  loadedFonts: Set<string>;
  loadFont: (fontName: string, fontUrl?: string) => Promise<boolean>;
  loadFonts: (fontNames: string[]) => Promise<void>;
  loadFontsWithUrls: (options: FontOption[]) => Promise<void>;
  registerFontUrl: (fontName: string, fontUrl: string) => void;
  isFontLoaded: (fontName: string) => boolean;
  isFontLoading: (fontName: string) => boolean;
  getFontFamilyForStyle: (fontName: string) => string;
}

/**
 * Get font URL from bundled asset
 * On web, require() returns the bundled URL with content hash
 */
const getFontUrlFromAsset = (asset: any): string | null => {
  if (!asset) return null;
  if (typeof asset === "string") return asset;
  if (asset.uri) return asset.uri;
  if (asset.default) {
    return typeof asset.default === "string"
      ? asset.default
      : asset.default?.uri;
  }
  return null;
};

/**
 * Inject @font-face CSS for web platform using a URL
 */
const injectWebFontFromUrl = (
  slug: string,
  fontFamily: string,
  fontUrl: string
): Promise<boolean> => {
  return new Promise((resolve) => {
    if (Platform.OS !== "web") {
      resolve(false);
      return;
    }

    const styleId = `engrave-font-${slug}`;
    if (document.getElementById(styleId)) {
      resolve(true);
      return;
    }

    // Determine the format based on file extension
    const isOtf = fontUrl.toLowerCase().endsWith(".otf");
    const format = isOtf ? "opentype" : "truetype";

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @font-face {
        font-family: '${fontFamily}';
        src: url('${fontUrl}') format('${format}');
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }
    `;
    document.head.appendChild(style);

    if (typeof FontFace !== "undefined") {
      const testFont = new FontFace(fontFamily, `url(${fontUrl})`);
      testFont
        .load()
        .then(() => {
          (document.fonts as any).add(testFont);
          resolve(true);
        })
        .catch((err) => {
          console.warn(
            `[useEngraveFonts] Web font load failed for ${slug} from URL:`,
            err
          );
          // Remove the style tag since font failed to load
          const styleEl = document.getElementById(styleId);
          if (styleEl) styleEl.remove();
          resolve(false);
        });
    } else {
      resolve(true);
    }
  });
};

/**
 * Inject @font-face CSS for web platform using bundled asset
 */
const injectWebFont = (slug: string, fontFamily: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (Platform.OS !== "web") {
      resolve(false);
      return;
    }

    const fontAsset = getNativeFontAsset(slug);
    const fontPath = getFontUrlFromAsset(fontAsset);

    if (!fontPath) {
      resolve(false);
      return;
    }

    // Use the URL-based loader
    injectWebFontFromUrl(slug, fontFamily, fontPath).then(resolve);
  });
};

/**
 * Load font on native using expo-font with remote URL
 */
const loadNativeFontFromUrl = async (
  slug: string,
  fontFamily: string,
  fontUrl: string
): Promise<boolean> => {
  if (Platform.OS === "web") return false;

  try {
    await Font.loadAsync({
      [fontFamily]: { uri: fontUrl },
    });
    return true;
  } catch (error) {
    console.error(
      `[useEngraveFonts] Failed to load native font from URL: ${slug}`,
      error
    );
    return false;
  }
};

/**
 * Load font on native using expo-font with bundled asset
 */
const loadNativeFont = async (
  slug: string,
  fontFamily: string
): Promise<boolean> => {
  if (Platform.OS === "web") return false;

  try {
    const fontAsset = getNativeFontAsset(slug);
    if (!fontAsset) {
      return false;
    }

    await Font.loadAsync({
      [fontFamily]: fontAsset,
    });

    return true;
  } catch (error) {
    console.error(
      `[useEngraveFonts] Failed to load native font: ${slug}`,
      error
    );
    return false;
  }
};

/**
 * Hook for managing engrave font loading
 * Supports both local bundled fonts and remote font URLs
 */
export const useEngraveFonts = (
  preloadFonts?: string[]
): UseEngraveFontsReturn => {
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [loadingFonts, setLoadingFonts] = useState<Set<string>>(new Set());
  const loadingPromises = useRef<Map<string, Promise<boolean>>>(new Map());

  // Store remote font URLs (fontName -> url)
  const fontUrlsRef = useRef<Map<string, string>>(new Map());

  /**
   * Register a remote font URL for a font name
   */
  const registerFontUrl = useCallback((fontName: string, fontUrl: string) => {
    if (fontName && fontUrl) {
      fontUrlsRef.current.set(fontName, fontUrl);
    }
  }, []);

  const isFontLoaded = useCallback(
    (fontName: string): boolean => {
      if (isSystemFont(fontName)) return true;
      const slug = fontNameToSlug(fontName);
      return loadedFonts.has(slug);
    },
    [loadedFonts]
  );

  const isFontLoading = useCallback(
    (fontName: string): boolean => {
      const slug = fontNameToSlug(fontName);
      return loadingFonts.has(slug);
    },
    [loadingFonts]
  );

  /**
   * Load a font by name, optionally with a remote URL
   * Priority: fontUrl parameter > registered URL > local bundled font
   */
  const loadFont = useCallback(
    async (fontName: string, fontUrl?: string): Promise<boolean> => {
      if (!fontName) return false;

      if (isSystemFont(fontName)) {
        return true;
      }

      const slug = fontNameToSlug(fontName);

      if (loadedFonts.has(slug)) {
        return true;
      }

      if (loadingPromises.current.has(slug)) {
        return loadingPromises.current.get(slug)!;
      }

      // Determine the URL to use (parameter > registered > none)
      const remoteUrl = fontUrl || fontUrlsRef.current.get(fontName);

      // If no remote URL and no local source, can't load
      if (!remoteUrl && !hasFontSource(fontName)) {
        console.warn(`[useEngraveFonts] No font source found for: ${fontName}`);
        return false;
      }

      const fontFamily = slugToFontFamily(slug);

      const loadPromise = (async () => {
        try {
          setLoadingFonts((prev) => new Set(prev).add(slug));

          let success = false;

          if (remoteUrl) {
            // Load from remote URL
            if (Platform.OS === "web") {
              success = await injectWebFontFromUrl(slug, fontFamily, remoteUrl);
            } else {
              success = await loadNativeFontFromUrl(
                slug,
                fontFamily,
                remoteUrl
              );
            }
          } else {
            // Fall back to local bundled font
            if (Platform.OS === "web") {
              success = await injectWebFont(slug, fontFamily);
            } else {
              success = await loadNativeFont(slug, fontFamily);
            }
          }

          if (success) {
            setLoadedFonts((prev) => new Set(prev).add(slug));
          }

          setLoadingFonts((prev) => {
            const next = new Set(prev);
            next.delete(slug);
            return next;
          });

          return success;
        } catch (error) {
          console.error(
            `[useEngraveFonts] Failed to load font: ${fontName}`,
            error
          );
          setLoadingFonts((prev) => {
            const next = new Set(prev);
            next.delete(slug);
            return next;
          });
          return false;
        } finally {
          loadingPromises.current.delete(slug);
        }
      })();

      loadingPromises.current.set(slug, loadPromise);
      return loadPromise;
    },
    [loadedFonts]
  );

  /**
   * Load multiple fonts by name (uses registered URLs or local fonts)
   */
  const loadFonts = useCallback(
    async (fontNames: string[]): Promise<void> => {
      const fontsToLoad = fontNames.filter((name) => {
        if (isSystemFont(name)) return false;
        // Load if has remote URL OR has local source
        return fontUrlsRef.current.has(name) || hasFontSource(name);
      });

      if (fontsToLoad.length === 0) return;

      await Promise.all(fontsToLoad.map((name) => loadFont(name)));
    },
    [loadFont]
  );

  /**
   * Load fonts from options array (registers URLs and loads fonts)
   * Use this when you have font options with optional fontUrl field
   */
  const loadFontsWithUrls = useCallback(
    async (options: FontOption[]): Promise<void> => {
      // First register all URLs
      options.forEach((opt) => {
        if (opt.value && opt.fontUrl) {
          registerFontUrl(opt.value, opt.fontUrl);
        }
      });

      // Then load all fonts
      const fontNames = options.map((opt) => opt.value).filter(Boolean);

      await loadFonts(fontNames);
    },
    [loadFonts, registerFontUrl]
  );

  const getFontFamilyForStyle = useCallback(
    (fontName: string): string => {
      if (!fontName) return "System";

      if (isSystemFont(fontName)) {
        return fontName;
      }

      const slug = fontNameToSlug(fontName);
      const isLoaded = loadedFonts.has(slug);
      const fontFamily = isLoaded ? slugToFontFamily(slug) : "System";

      return fontFamily;
    },
    [loadedFonts]
  );

  useEffect(() => {
    const loadPreloadFonts = async () => {
      if (preloadFonts && preloadFonts.length > 0) {
        await loadFonts(preloadFonts);
      }
      setFontsLoaded(true);
    };

    loadPreloadFonts();
  }, []);

  return {
    fontsLoaded,
    loadingFonts,
    loadedFonts,
    loadFont,
    loadFonts,
    loadFontsWithUrls,
    registerFontUrl,
    isFontLoaded,
    isFontLoading,
    getFontFamilyForStyle,
  };
};

export default useEngraveFonts;
